import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import TaskDetailDrawer from "@/components/TaskDetailDrawer";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import { formatWIB, rupiah } from "@/lib/format";
import { cn } from "@/lib/utils";
import { CheckCircle2, FileWarning, GitBranch, Clock, Inbox } from "lucide-react";
import { toast } from "sonner";

const DECISIONS = [
  { key: "terima", label: "Terima laporan" },
  { key: "minta_perbaiki", label: "Minta perbaiki laporan" },
  { key: "setujui_eta", label: "Setujui estimasi selesai baru" },
  { key: "reassign", label: "Alihkan tugas (reassign)" },
  { key: "eskalasi", label: "Eskalasi ke atasan" },
  { key: "jadikan_agenda", label: "Jadikan agenda rapat" },
];

function LateReportReview({ report, onDone }) {
  const [decisions, setDecisions] = useState([]);
  const [note, setNote] = useState("");
  const [fairness, setFairness] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = (k) => setDecisions((d) => (d.includes(k) ? d.filter((x) => x !== k) : [...d, k]));

  const submit = async () => {
    if (decisions.length === 0) return toast.error("Pilih minimal 1 keputusan");
    setBusy(true);
    try {
      await api.post(`/late-reports/${report.id}/review`, { decisions, note, fairness: fairness || null });
      toast.success("Tinjauan laporan tersimpan");
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    setBusy(false);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`late-review-${report.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-bold text-slate-800">{report.task_title || "Tugas"}</p>
          <p className="text-xs text-slate-400">Oleh {report.reporter_name} · {formatWIB(report.created_at)}</p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-200 px-2.5 py-0.5 text-xs font-semibold text-rose-700"><FileWarning className="h-3 w-3" />Menunggu Tinjauan</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 text-sm mb-3">
        <div><p className="text-xs text-slate-400">Penyebab</p><p className="text-slate-700">{[...(report.categories||[]), ...(report.additional_categories||[])].join(", ")}</p></div>
        <div><p className="text-xs text-slate-400">Sifat</p><p className="text-slate-700">{report.controllable === "internal" ? "Dapat dikendalikan" : "Di luar kendali"}</p></div>
        <div className="sm:col-span-2"><p className="text-xs text-slate-400">Kronologi</p><p className="text-slate-700">{report.chronology}</p></div>
        {report.new_eta && <div><p className="text-xs text-slate-400">ETA Baru</p><p className="text-slate-700">{formatWIB(report.new_eta)}</p></div>}
        <div><p className="text-xs text-slate-400">Dampak Finansial</p><p className="text-slate-700">{rupiah(report.financial_impact)} · {report.impact_level}</p></div>
      </div>
      <div className="border-t border-slate-100 pt-3">
        <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Keputusan Atasan</p>
        <div className="grid sm:grid-cols-2 gap-1.5 mb-3">
          {DECISIONS.map((d) => (
            <label key={d.key} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={decisions.includes(d.key)} onCheckedChange={() => toggle(d.key)} data-testid={`decision-${d.key}`} />{d.label}
            </label>
          ))}
        </div>
        <div className="flex items-center gap-4 mb-3">
          <span className="text-sm text-slate-500">Penilaian:</span>
          <RadioGroup value={fairness} onValueChange={setFairness} className="flex gap-4">
            <label className="flex items-center gap-1.5 text-sm"><RadioGroupItem value="wajar" />Wajar</label>
            <label className="flex items-center gap-1.5 text-sm"><RadioGroupItem value="tidak_wajar" />Tidak Wajar</label>
          </RadioGroup>
        </div>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan (wajib jika minta perbaiki)" rows={2} className="mb-2" />
        <Button size="sm" onClick={submit} disabled={busy} data-testid={`late-review-submit-${report.id}`} className="bg-indigo-600 hover:bg-indigo-700">Simpan Tinjauan</Button>
      </div>
    </div>
  );
}

export default function ApprovalInbox() {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const load = () => api.get("/approvals/inbox").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const open = (id) => { setOpenId(id); setDrawerOpen(true); };

  if (!data) return <div className="text-slate-400 py-10 text-center">Memuat...</div>;
  const c = data.counts;

  const CountBadge = ({ n }) => n > 0 ? <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-rose-500 text-white font-bold">{n}</span> : null;

  const lintasAction = async (id, action) => {
    try { await api.post(`/tasks/${id}/status`, { action, note: action === "tolak_lintas" ? "Ditolak Kepala Divisi" : null }); toast.success("Diproses"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><Inbox className="h-7 w-7 text-indigo-500" />Kotak Persetujuan</h1>
        <p className="text-slate-500 text-sm">Semua item yang menunggu keputusan Anda</p>
      </div>

      <Tabs defaultValue="verifikasi">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="verifikasi" data-testid="approval-inbox-tab-verifikasi">Verifikasi Tugas<CountBadge n={c.verifikasi_tugas} /></TabsTrigger>
          <TabsTrigger value="keterlambatan" data-testid="approval-inbox-tab-keterlambatan">Laporan Keterlambatan<CountBadge n={c.laporan_keterlambatan} /></TabsTrigger>
          <TabsTrigger value="lintas" data-testid="approval-inbox-tab-lintas">Lintas Divisi<CountBadge n={c.lintas_divisi} /></TabsTrigger>
        </TabsList>

        <TabsContent value="verifikasi">
          {data.verifikasi_tugas.length === 0 ? <Empty msg="Tidak ada tugas menunggu verifikasi." /> : (
            <div className="space-y-3">
              {data.verifikasi_tugas.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{t.title}</p>
                    <p className="text-xs text-slate-400">{t.assignee?.name} · {t.branch?.name} · progres {t.progress}%</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <PriorityBadge priority={t.priority} />
                    <Button size="sm" onClick={() => open(t.id)} data-testid={`verify-open-${t.id}`}><CheckCircle2 className="h-4 w-4 mr-1" />Tinjau</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="keterlambatan">
          {data.laporan_keterlambatan.length === 0 ? <Empty msg="Tidak ada laporan keterlambatan menunggu." /> : (
            <div className="space-y-4">{data.laporan_keterlambatan.map((r) => <LateReportReview key={r.id} report={r} onDone={load} />)}</div>
          )}
        </TabsContent>

        <TabsContent value="lintas">
          {data.lintas_divisi.length === 0 ? <Empty msg="Tidak ada permintaan lintas divisi." /> : (
            <div className="space-y-3">
              {data.lintas_divisi.map((t) => (
                <div key={t.id} className="rounded-2xl border border-fuchsia-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 truncate flex items-center gap-1.5"><GitBranch className="h-4 w-4 text-fuchsia-500" />{t.title}</p>
                      <p className="text-xs text-slate-400">Dari {t.giver?.name} → {t.assignee?.name || t.assignee_position} · {t.branch?.name}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button size="sm" onClick={() => lintasAction(t.id, "terima_lintas")} data-testid={`lintas-terima-${t.id}`} className="bg-emerald-600 hover:bg-emerald-700">Terima</Button>
                      <Button size="sm" variant="outline" onClick={() => lintasAction(t.id, "tolak_lintas")} className="text-rose-600 border-rose-200">Tolak</Button>
                    </div>
                  </div>
                  {t.description && <p className="text-sm text-slate-500 mt-2">{t.description}</p>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <TaskDetailDrawer taskId={openId} open={drawerOpen} onOpenChange={setDrawerOpen} onChanged={load} />
    </div>
  );
}

function Empty({ msg }) {
  return <div className="text-center py-16 text-slate-400"><CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-slate-300" />{msg}</div>;
}
